import { memo } from 'react';

import { TableCell, tableCellClasses } from '@kc-console/common-ui';
import { t } from '@kc-console/core';

import DataTableCheckbox from './DataTableCheckbox';
import DataTableRadio from './DataTableRadio';
import { createBuiltinColumnVariableKeys } from './utils';

import type { RowSelectionState, Updater } from '@tanstack/react-table';

export interface DataTableTableBodySelectionCellProps {
  hasSelectionColumn: boolean;
  hasSelectionColumnAsCheckbox: boolean;
  hasSelectionColumnAsRadio: boolean;
  onRowSelectionChange: (updater: Updater<RowSelectionState>) => void;
  onRowSelectionToggle: (rowId: string) => void;
  rowId: string;
  selected: boolean;
  selectionDisabled: boolean;
  selectionHidden: boolean;
}

export default memo(function DataTableTableBodySelectionCell(
  props: DataTableTableBodySelectionCellProps,
) {
  const {
    hasSelectionColumn,
    hasSelectionColumnAsCheckbox,
    hasSelectionColumnAsRadio,
    onRowSelectionChange,
    onRowSelectionToggle,
    rowId,
    selected,
    selectionDisabled,
    selectionHidden,
  } = props;

  const handleTableCellClick = (e: React.MouseEvent<HTMLTableCellElement>) => {
    e.stopPropagation();
    if (selectionDisabled || selectionHidden) return;
    if (hasSelectionColumnAsCheckbox) {
      onRowSelectionToggle(rowId);
    }
    if (hasSelectionColumnAsRadio) {
      onRowSelectionChange({ [rowId]: true });
    }
  };

  return (
    <>
      {hasSelectionColumn && (
        <TableCell
          padding="checkbox"
          size="small"
          sticky
          stickyPosition="left"
          onClick={handleTableCellClick}
          style={{
            left: `var(${createBuiltinColumnVariableKeys('selection').offset})`,
          }}
        >
          {!selectionHidden && hasSelectionColumnAsCheckbox && (
            // FIXME (@walter-24) checkbox가 disabled인 경우에도 tr hover에 대한 체크박스 스타일이 적용되는 버그 수정 필요 (https://github.kakaoenterprise.in/walter-24/kc-console/blob/6e464a3eef4c818d549e93392b1251c0e4553cfa/packages/common-ui/src/components/TableRow/theme.ts#L83)
            <DataTableCheckbox
              aria-label={t('common:common.label.selected_items', { count: rowId })}
              disabled={selectionDisabled}
              checked={selected}
              onClick={e => {
                e.stopPropagation();
                if (selectionDisabled) return;
                onRowSelectionToggle(rowId);
              }}
              selectorOverrides={{
                active: `td:active > .${tableCellClasses.root} &`,
                hover: `td:hover > .${tableCellClasses.root} &`,
              }}
            />
          )}
          {!selectionHidden && hasSelectionColumnAsRadio && (
            <DataTableRadio
              aria-label={t('common:common.label.selected_items', { count: rowId })}
              disabled={selectionDisabled}
              checked={selected}
              onClick={e => {
                e.stopPropagation();
                if (selectionDisabled) return;
                onRowSelectionChange({ [rowId]: true });
              }}
            />
          )}
        </TableCell>
      )}
    </>
  );
});
